import axios from 'axios';
import React, { ReactElement, VFC, useCallback, useState } from 'react';

// 표준 금융기관코드(3자리) → KCP 은행코드 변환
// F2F KcpCmsHttpClient.formatBankCode() 규칙과 동일:
//   코드값 ≤ 92 → "BK" + 뒤 2자리 (예: 088 → BK88)
//   코드값 > 92 → "B" + 3자리 전체 (예: 209 → B209)
const formatKcpBankCode = (bankCode: string): string | null => {
  const num = parseInt(bankCode, 10);
  if (isNaN(num)) return null;
  // 신한은행(088)은 KCP 코드 BK26 (DB TB_FINANCIAL_INSTITUTION_CODE 기준)
  if (bankCode === '088') return 'BK26';
  if (num <= 92) {
    return `BK${bankCode.slice(-2)}`;
  }
  return `B${bankCode}`;
};

interface KcpAccountCheckParams {
  name: string;
  account: string;
  bankCode: string;
}

interface KcpAccountCheckProps {
  onSuccess?: () => void;
  onError?: (message: string) => void;
  onBlocked?: () => void;
  renderSubmitButton: (props: { onClick: () => void }) => ReactElement;
  getParams: () => KcpAccountCheckParams;
}

const KcpAccountCheck: VFC<KcpAccountCheckProps> = ({
  renderSubmitButton,
  onSuccess,
  onError,
  onBlocked,
  getParams,
}) => {
  const [loading, setLoading] = useState(false);

  const start = useCallback(async () => {
    const params = getParams();

    if (!params.name) {
      onError?.(`예금주명은 필수입니다.`);
      return;
    }
    if (!params.account) {
      onError?.(`계좌번호는 필수입니다.`);
      return;
    }
    if (!params.bankCode) {
      onError?.(`은행은 필수입니다.`);
      return;
    }

    const kcpBankCode = formatKcpBankCode(params.bankCode);
    if (!kcpBankCode) {
      onError?.(`유효하지 않은 은행코드입니다.`);
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.post(
        `/unicef/api/fo/common/kcpAccountCheck`,
        {
          mod_depositor: params.name,
          mod_account: params.account,
          mod_bankcode: kcpBankCode,
        },
      );

      if (data.resultCode === 'blocked') {
        onBlocked?.();
        onError?.(data.resultMessage || `사용할 수 없는 계좌입니다.`);
        return;
      }

      if (data.resultCode === 'success') {
        onSuccess?.();
      } else {
        onError?.(data.resultMessage || `계좌인증에 실패하였습니다.`);
      }
    } catch (e) {
      onError?.(`계좌인증 처리 중 오류가 발생하였습니다.`);
    } finally {
      setLoading(false);
    }
  }, [getParams, onError, onBlocked, onSuccess]);

  return renderSubmitButton({ onClick: loading ? () => {} : start });
};

export default KcpAccountCheck;
